import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-hot-toast";

const getEmployeeLabel = (employee) =>
  `${employee.old_emp_code || employee.emp_code || "—"} - ${
    employee.display_name ||
    `${employee.first_name || ""} ${employee.last_name || ""}`.trim()
  }`;

const toNumber = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : NaN;
};

const MODE_TITLES = {
  initial: "Add Initial Salary",
  increment: "Add Salary Increment",
  adjustment: "Add Salary Adjustment",
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad2 = (value) => String(value).padStart(2, "0");

// Parsed manually (not via Date) to avoid UTC/local timezone day-shift issues.
const parseDateParts = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
  if (!match) return null;
  const [, year, month] = match;
  return { year: Number(year), month: Number(month) };
};

// The next calendar month after the active salary's effective_from — the earliest valid increment month.
const getNextMonth = (year, month) =>
  month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };

const SalaryForm = ({ mode, employees, activeSalary, onSubmit, close }) => {
  const isInitial = mode === "initial";
  const isIncrement = mode === "increment";
  const isAdjustment = mode === "adjustment";

  const today = new Date();

  const [employeeSearch, setEmployeeSearch] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [gross, setGross] = useState("");
  const [increment, setIncrement] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(
    today.toISOString().split("T")[0]
  );
  const [effectiveMonth, setEffectiveMonth] = useState(today.getMonth() + 1);
  const [effectiveYear, setEffectiveYear] = useState(today.getFullYear());
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if ((isIncrement || isAdjustment) && activeSalary) {
      setSelectedEmployeeId(activeSalary.employee_id);
      setIncrement("");

      if (isAdjustment) {
        setGross(activeSalary.gross ?? "");
        setEffectiveFrom(activeSalary.effective_from ?? "");
      }

      if (isIncrement) {
        const activeParts = parseDateParts(activeSalary.effective_from);
        if (activeParts) {
          const minMonth = getNextMonth(activeParts.year, activeParts.month);
          setEffectiveYear(minMonth.year);
          setEffectiveMonth(minMonth.month);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, activeSalary]);

  const filteredEmployees = useMemo(() => {
    if (!isInitial) return [];
    const search = employeeSearch.toLowerCase().trim();
    if (!search) return employees.slice(0, 20);

    return employees
      .filter((employee) => getEmployeeLabel(employee).toLowerCase().includes(search))
      .slice(0, 20);
  }, [employees, employeeSearch, isInitial]);

  const currentGross = toNumber(activeSalary?.total_gross);
  // Adjustment keeps the previous increment unchanged; only gross is user-editable.
  const activeIncrement = Number.isFinite(toNumber(activeSalary?.increment))
    ? toNumber(activeSalary.increment)
    : 0;
  const computedAdjustmentRevisedGross =
    isAdjustment && Number.isFinite(toNumber(gross))
      ? toNumber(gross) + activeIncrement
      : null;
  const computedRevisedGross =
    isIncrement && Number.isFinite(currentGross) && Number.isFinite(toNumber(increment))
      ? currentGross + toNumber(increment)
      : null;

  const incrementEffectiveFrom = `${effectiveYear}-${pad2(effectiveMonth)}-01`;

  const minIncrementDate = useMemo(() => {
    const activeParts = parseDateParts(activeSalary?.effective_from);
    return activeParts ? getNextMonth(activeParts.year, activeParts.month) : null;
  }, [activeSalary]);

  const handleSelectEmployee = (employee) => {
    setSelectedEmployeeId(employee.id || employee.employee_id);
    setEmployeeSearch(getEmployeeLabel(employee));
  };

  const buildPayload = () => {
    if (isInitial) {
      return {
        employee_id: Number(selectedEmployeeId),
        gross: gross ? String(gross) : "",
        increment: 0,
        effective_from: effectiveFrom,
        effective_to: null,
        revision_type: "INITIAL",
        status: 1,
        notes: null,
      };
    }

    if (isIncrement) {
      return {
        employee_id: Number(activeSalary.employee_id),
        gross: computedRevisedGross !== null ? String(computedRevisedGross) : "",
        increment: increment ? String(increment) : "",
        effective_from: incrementEffectiveFrom,
        effective_to: null,
        revision_type: "INCREMENT",
        status: 1,
        notes: null,
      };
    }

    return {
      employee_id: Number(activeSalary.employee_id),
      gross: computedAdjustmentRevisedGross !== null ? String(computedAdjustmentRevisedGross) : "",
      increment: String(activeIncrement),
      effective_from: effectiveFrom,
      effective_to: null,
      revision_type: "ADJUSTMENT",
      status: 1,
      notes: notes.trim(),
    };
  };

  const validate = () => {
    if (isInitial) {
      if (!selectedEmployeeId) return "Please select an employee.";
      if (!Number.isFinite(toNumber(gross)) || toNumber(gross) <= 0)
        return "Gross must be a number greater than zero.";
      if (!effectiveFrom) return "Effective-from date is required.";
      return null;
    }

    if (isIncrement) {
      if (!Number.isFinite(toNumber(increment)) || toNumber(increment) <= 0)
        return "Increment must be greater than zero.";
      return null;
    }

    // adjustment
    if (!Number.isFinite(toNumber(gross)) || toNumber(gross) <= 0)
      return "Gross must be a number greater than zero.";
    if (!effectiveFrom) return "Effective-from date is required.";
    if (!notes.trim()) return "Notes are required for an adjustment.";

    const unchanged =
      toNumber(gross) === toNumber(activeSalary?.gross) &&
      effectiveFrom === activeSalary?.effective_from;

    if (unchanged) return "Change at least one value before submitting an adjustment.";

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(buildPayload());
    } finally {
      setSubmitting(false);
    }
  };

  const yearOptions = useMemo(() => {
    const currentYear = today.getFullYear();
    const startYear = minIncrementDate ? minIncrementDate.year : currentYear - 1;
    const endYear = Math.max(currentYear, startYear) + 5;
    const years = [];
    for (let y = startYear; y <= endYear; y += 1) {
      years.push(y);
    }
    return years;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minIncrementDate]);

  const monthOptions = useMemo(() => {
    if (!isIncrement || !minIncrementDate || effectiveYear > minIncrementDate.year) {
      return MONTH_NAMES.map((name, index) => ({ name, value: index + 1 }));
    }

    return MONTH_NAMES
      .map((name, index) => ({ name, value: index + 1 }))
      .filter((option) => option.value >= minIncrementDate.month);
  }, [isIncrement, minIncrementDate, effectiveYear]);

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header bg-light">
              <h5 className="modal-title fw-semibold">{MODE_TITLES[mode]}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={close}
                disabled={submitting}
              ></button>
            </div>

            <div className="modal-body">
              {isInitial ? (
                <div className="mb-3 position-relative">
                  <label className="form-label">Employee</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by employee code or name..."
                    value={employeeSearch}
                    onChange={(e) => {
                      setEmployeeSearch(e.target.value);
                      setSelectedEmployeeId("");
                    }}
                    autoComplete="off"
                  />
                  {employeeSearch && !selectedEmployeeId && (
                    <div
                      className="list-group position-absolute w-100 shadow-sm"
                      style={{ zIndex: 1060, maxHeight: "200px", overflowY: "auto" }}
                    >
                      {filteredEmployees.length === 0 ? (
                        <span className="list-group-item text-muted small">
                          No matching employees.
                        </span>
                      ) : (
                        filteredEmployees.map((employee) => (
                          <button
                            type="button"
                            key={employee.id || employee.employee_id}
                            className="list-group-item list-group-item-action small"
                            onClick={() => handleSelectEmployee(employee)}
                          >
                            {getEmployeeLabel(employee)}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-3">
                  <label className="form-label">Employee</label>
                  <input
                    type="text"
                    className="form-control"
                    value={`${activeSalary?.emp_code || "—"} - ${
                      activeSalary?.employee_name || "—"
                    }`}
                    disabled
                  />
                </div>
              )}

              {(isIncrement || isAdjustment) && (
                <div className="mb-3">
                  <label className="form-label">Current Gross</label>
                  <input
                    type="text"
                    className="form-control"
                    value={activeSalary?.total_gross ?? "—"}
                    disabled
                  />
                </div>
              )}

              {!isIncrement && (
                <div className="mb-3">
                  <label className="form-label">Gross</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    value={gross}
                    onChange={(e) => setGross(e.target.value)}
                    required
                  />
                </div>
              )}

              {isIncrement && (
                <div className="mb-3">
                  <label className="form-label">Increment</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    value={increment}
                    onChange={(e) => setIncrement(e.target.value)}
                    required
                  />
                </div>
              )}

              {isAdjustment && (
                <div className="mb-3">
                  <label className="form-label">Increment</label>
                  <input
                    type="text"
                    className="form-control"
                    value={activeIncrement.toFixed(2)}
                    disabled
                  />
                </div>
              )}

              {isIncrement && (
                <div className="mb-3">
                  <label className="form-label">Revised Gross</label>
                  <input
                    type="text"
                    className="form-control"
                    value={computedRevisedGross !== null ? computedRevisedGross.toFixed(2) : ""}
                    disabled
                  />
                </div>
              )}

              {isAdjustment && (
                <div className="mb-3">
                  <label className="form-label">Revised Gross</label>
                  <input
                    type="text"
                    className="form-control"
                    value={
                      computedAdjustmentRevisedGross !== null
                        ? computedAdjustmentRevisedGross.toFixed(2)
                        : ""
                    }
                    disabled
                  />
                </div>
              )}

              {isIncrement ? (
                <div className="row">
                  <div className="col-6 mb-3">
                    <label className="form-label">Effective From — Month</label>
                    <select
                      className="form-select"
                      value={effectiveMonth}
                      onChange={(e) => setEffectiveMonth(Number(e.target.value))}
                    >
                      {monthOptions.map((option) => (
                        <option key={option.name} value={option.value}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6 mb-3">
                    <label className="form-label">Effective From — Year</label>
                    <select
                      className="form-select"
                      value={effectiveYear}
                      onChange={(e) => {
                        const nextYear = Number(e.target.value);
                        setEffectiveYear(nextYear);
                        if (minIncrementDate && nextYear === minIncrementDate.year && effectiveMonth < minIncrementDate.month) {
                          setEffectiveMonth(minIncrementDate.month);
                        }
                      }}
                    >
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="mb-3">
                  <label className="form-label">Effective From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                    required
                  />
                </div>
              )}

              {isAdjustment && (
                <div className="mb-3">
                  <label className="form-label">
                    Notes <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Reason for this correction..."
                    required
                  />
                </div>
              )}
            </div>

            <div className="modal-footer bg-light">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={close}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

SalaryForm.propTypes = {
  mode: PropTypes.oneOf(["initial", "increment", "adjustment"]).isRequired,
  employees: PropTypes.array,
  activeSalary: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
};

SalaryForm.defaultProps = {
  employees: [],
  activeSalary: null,
};

export default SalaryForm;
